import * as React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Drawer, DrawerContent, DrawerItem, DrawerSelectEvent } from '@progress/kendo-react-layout';
import { Button } from '@progress/kendo-react-buttons';
import { Window } from '@progress/kendo-react-dialogs';
import {
    AppBar,
    AppBarSection,
    AppBarSpacer
} from "@progress/kendo-react-layout";
import { useMsal } from '@azure/msal-react';
import { SvgIcon } from '@progress/kendo-react-common';
import * as svgIcons from '@progress/kendo-svg-icons';

import { Footer } from "./Footer";
import { DecommisionedAnnouncement, MaintenceAnnouncement } from './Announcement';
import { loginRequest, protectedResources } from '../authConfig';
import { useFetchWithMsal } from '../hooks/useFetchWithMsal';
import { useEffect, useState } from 'react';
import { LoadingScreen } from './LoadingScreen';
import { AppUserContext, LabContext } from './PvsContextProvider';
import { StreckLogo } from '../assets/lab-icons/kendo/CustomIcons';
import NotificationPopup from '../components/NotificationPopup';
import AcceptLabInvites from '../pages/labInvites/Lab_Invite_Accept';
import { Notifications } from './dashboard/Notifications';
import { gaEvents } from './GoogleAnalytics';

const CustomDropdownItem = (props: any) => {
    const { visible, ...others } = props;
    const arrowDir = props["data-expanded"]
        ? "k-i-arrow-chevron-down"
        : "k-i-arrow-chevron-right";
    return (
        <React.Fragment>
            {props.visible === false ? null : (
                <DrawerItem {...others}>
                    {props["identation-level"] === 1 &&
                        <>&emsp;</>
                    }
                    <span> <SvgIcon icon={props.icon} ></SvgIcon> </span>
                    <span className={"k-item-text"}>{props.text}</span>
                    {props["data-expanded"] !== undefined && (
                        <span
                            className={"k-icon " + arrowDir}
                        />
                    )}
                </DrawerItem>
            )}
        </React.Fragment>
    );
};

interface DrawerContainerShellProps {
    appUser?: any,
    handleLogOutClick?: Function,
    handleDrawerExpandClick?: Function,
    children?: React.ReactNode
}

const SubmittedKitListSideBarId = 51;
const SubmitAKitSideBarId = 55;
const ReportSideBarId = 7;
const AuditSectionSideBarStartId = 1000;

export const DrawerContainerShell = (props: DrawerContainerShellProps) => {
    const { instance } = useMsal();

    return (
        <div className='page-container' >
            <NotificationPopup />
            <div className='content-wrap'>
                <React.Fragment>
                    <AppBar>
                        {props.appUser !== null &&
                            <>
                                <AppBarSection>
                                    <button className="k-button k-button-md k-rounded-md k-button-flat k-button-flat-base" onClick={() => props.handleDrawerExpandClick?.()}>
                                        <span className="k-icon k-i-menu" />
                                    </button>
                                </AppBarSection>

                                <AppBarSpacer style={{ width: 4 }} />
                            </>}

                        <AppBarSection>
                            {/* Need to figure out how to import SVGs in a good manner https://blog.logrocket.com/how-to-use-svgs-react/ */}
                            <StreckLogo />
                        </AppBarSection>

                        <AppBarSpacer style={{ width: 32 }} />

                        <AppBarSection>
                            <h1 className="title">PVS {process.env.REACT_APP_ENV === "PROD" ? "" : "Internal " + process.env.REACT_APP_ENV}</h1>
                        </AppBarSection>

                        <AppBarSpacer />

                        {props.appUser !== null &&
                            <>
                                <AppBarSection className="actions">
                                    <div className="user-email">{props.appUser?.Email}</div>
                                </AppBarSection>

                                <AppBarSection>
                                    <span className="k-appbar-separator" />
                                </AppBarSection>

                                <AppBarSection>
                                    <Notifications></Notifications>
                                </AppBarSection>

                                <AppBarSection>
                                    <span className="k-appbar-separator" />
                                </AppBarSection>
                            </>}

                        <AppBarSection>
                            <div className='user-sign-io' >
                                <Link to="/" style={{ textDecoration: 'none' }}>
                                    {props.appUser ?
                                        <Button className="user-button k-button k-button-md k-rounded-md k-button-solid k-button-solid-base k-button-solid-primary" onClick={() => props.handleLogOutClick?.()}>
                                            Sign Out
                                        </Button> :
                                        <Button className="k-button-solid-primary" onClick={() => instance.loginRedirect(loginRequest)}>Sign In</Button>
                                    }
                                </Link>
                            </div>
                        </AppBarSection>
                    </AppBar>
                </React.Fragment>
                {props.children}
            </div>
            <Footer></Footer>
        </div>);
}

interface DrawerContainerProps {
    handleLogOut: Function,
    children?: React.ReactNode
}

export const DrawerContainer = (props: DrawerContainerProps) => {

    // Yes its angular but has a search location https://www.telerik.com/kendo-angular-ui/components/icons/svgicon/svgicon-list/
    // React without search https://www.telerik.com/kendo-react-ui/components/utils/svgicon/#toc-svg-icons-list
    const allIcons = svgIcons;

    const appUserContext = React.useContext(AppUserContext);
    const labContext = React.useContext(LabContext);
    const navigate = useNavigate();

    const itemArray = [
        {
            text: 'Home',
            ['identation-level']: 0,
            id: 1,
            selected: true,
            route: '/home',
            icon: allIcons.homeIcon,
        },
        {
            text: 'Labs',
            ['identation-level']: 0,
            id: 2,
            ['data-expanded']: false,
            icon: allIcons.globeIcon,
        },
        {
            text: 'Lab List',
            ['identation-level']: 1,
            id: 21,
            parentId: 2,
            route: '/lab-list',
            icon: allIcons.jsIcon,
        },
        {
            text: 'Create New Lab',
            ['identation-level']: 1,
            id: 22,
            parentId: 2,
            route: '/lab-manage',
            icon: allIcons.plusIcon,
        },
        {
            text: 'Operators',
            ['identation-level']: 0,
            id: 3,
            ['data-expanded']: false,
            icon: allIcons.cloudIcon,
        },
        {
            text: 'Operator List',
            ['identation-level']: 1,
            id: 31,
            parentId: 3,
            route: '/operator-list',
            icon: allIcons.jsIcon,
        },
        {
            text: 'Create New Operator',
            ['identation-level']: 1,
            id: 32,
            parentId: 3,
            route: '/operator-manage',
            icon: allIcons.plusIcon,
        },
        {
            text: 'Pipets',
            ['identation-level']: 0,
            id: 4,
            ['data-expanded']: false,
            icon: allIcons.eyedropperIcon,
        },
        {
            text: 'Pipet List',
            ['identation-level']: 1,
            id: 41,
            parentId: 4,
            route: '/pipet-list',
            icon: allIcons.jsIcon,
        },
        {
            text: 'Create New Pipet',
            ['identation-level']: 1,
            id: 42,
            parentId: 4,
            route: '/pipet-manage',
            icon: allIcons.plusIcon,
        },
        {
            text: 'Submitted Kits',
            ['identation-level']: 0,
            id: 5,
            ['data-expanded']: false,
            icon: allIcons.inboxIcon,
        },
        {
            text: 'Submitted Kit List',
            ['identation-level']: 1,
            id: SubmittedKitListSideBarId,
            parentId: 5,
            route: '/submitted-kit-list',
            icon: allIcons.jsIcon,
        },
        {
            text: 'Submit New Kit',
            ['identation-level']: 1,
            id: SubmitAKitSideBarId,
            parentId: 5,
            route: '/submitted-kit-manage',
            icon: allIcons.plusIcon,
        },
        {
            text: 'Reports',
            ['identation-level']: 0,
            id: ReportSideBarId,
            route: '/report-list',
            icon: allIcons.reportElementIcon,
        },
        {
            separator: true,
            ['identation-level']: 1,
            id: 95
        },
        {
            text: 'Settings',
            ['identation-level']: 0,
            id: 96,
            route: '/settings/',
            icon: allIcons.gearsIcon
        },
        {
            text: 'FAQ',
            ['identation-level']: 0,
            id: 97,
            route: '/FAQ/',
            icon: allIcons.questionCircleIcon
        }
    ];

    const [items, setItems] = React.useState<Array<any>>(itemArray);

    const [showLabInviteWindow, setShowLabInviteWindow] = useState(false);
    const [activeLabInvites, setActiveLabInvites] = useState([] as any[]);
    const [initialized, setInitialized] = useState(false);
    const [drawerExpanded, setDrawerExpanded] = React.useState<boolean>(true);

    const { isLoading, execute } = useFetchWithMsal({
        scopes: protectedResources.scopes.admin
    });

    const checkForLabInvites = () => {
        execute("Get", protectedResources.apiLabInvite.endpoint + "CheckForLabInvites")
            .then((result: any) => {
                if (result) {
                    setShowLabInviteWindow(true);
                    setActiveLabInvites(result);
                }
            });
    }

    const getLabList = () => {
        execute("Get", protectedResources.apiLab.endpoint + "GetAllLabs")
            .then((result: any) => {
                labContext.setData(result);
                checkForLabInvites()
            });
    }

    const handleDrawerExpandClick = () => {
        setDrawerExpanded(!drawerExpanded);
    };

    const { instance } = useMsal();

    const handleLogOutClick = () => {
        props.handleLogOut();
        gaEvents.eventLogOut();
    };

    const onSelect = (ev: DrawerSelectEvent) => {
        const currentItem = ev.itemTarget.props;
        const isParent = currentItem["data-expanded"] !== undefined;
        const nextExpanded = !currentItem["data-expanded"];

        const newData = items.map((item) => {
            const {
                selected,
                ["data-expanded"]: currentExpanded,
                id,
                ...others
            } = item;
            const isCurrentItem = currentItem.id === id;
            return {
                selected: isCurrentItem,
                ["data-expanded"]:
                    isCurrentItem && isParent ? nextExpanded : currentExpanded,
                id,
                ...others,
            };
        });
        navigate(ev.itemTarget.props.route);
        setItems(newData);
    };

    const data = items.map((item) => {
        const { parentId, ...others } = item;
        if (parentId !== undefined) {
            const parent = items.find((parent) => parent.id === parentId);
            return {
                ...others,
                visible: parent["data-expanded"],
            };
        }
        return item;
    });

    const getContent = () => {
        if (!instance.getActiveAccount()) {
            return <div>
                Please log in to continue.
            </div>;
        }

        if (initialized && !isLoading) {
            if (!labContext.data) {
                if (!window.location.href.endsWith('onboarding')) {
                    setTimeout(() => navigate('/onboarding'), 250);
                    return <div>Redirecting to Onboarding...</div>;
                } else {
                    return props.children;
                }
            } else {
                return props.children;
            }
        }

        return <LoadingScreen></LoadingScreen>;
    }

    React.useEffect(() => {
        if (labContext.data?.length === 1) {
            const newItems = [...items];
            const currentLab = labContext.data[0];
            newItems[2].text = currentLab.facilityName;
            newItems[2].route = '/lab-manage/' + currentLab.id;
            setItems(newItems);
        } else {
            const newItems = [...items];
            newItems[2].text = 'Lab List';
            newItems[2].route = '/lab-list/';
            setItems(newItems);
        }
    }, [labContext.data]);

    React.useEffect(() => {
        const auditSectionSideBarIndex = itemArray.findIndex((x: any) => x.id === AuditSectionSideBarStartId);
        if (appUserContext.adminView && auditSectionSideBarIndex === -1) {
            const submittedKitListIndex = itemArray.findIndex((x: any) => x.id === SubmittedKitListSideBarId);
            itemArray.splice(submittedKitListIndex + 1, 0, {
                text: 'Kits to Process',
                ['identation-level']: 1,
                id: 52,
                parentId: 5,
                route: '/submitted-kit-list-processing',
                icon: allIcons.jsIcon,
            }, {
                text: 'Kits to Review',
                ['identation-level']: 1,
                id: 53,
                parentId: 5,
                route: '/submitted-kit-list-review',
                icon: allIcons.jsIcon,
            }, {
                text: 'Completed Kits',
                ['identation-level']: 1,
                id: 54,
                parentId: 5,
                route: '/submitted-kit-list-completed',
                icon: allIcons.jsIcon,
            });

            const AuditSection = {
                text: 'Audits',
                ['data-expanded']: false,
                ['identation-level']: 0,
                id: AuditSectionSideBarStartId,
                icon: allIcons.jsIcon,
            };

            const auditInsertIndex = itemArray.findIndex((x: any) => x.id === SubmitAKitSideBarId);
            let auditChilidId = AuditSection.id;
            const addAuditItem = (title: string, route: string) => {
                auditChilidId += 1;
                return {
                    text: title,
                    ['identation-level']: 1,
                    id: auditChilidId,
                    parentId: AuditSection.id,
                    route: route,
                    icon: allIcons.jsIcon,
                };
            }

            const auditSideBarMenu = [
                AuditSection,
                addAuditItem('App User', '/app-user-audit-list'),
                addAuditItem('App User Lab', '/app-user-lab-audit-list'),
                addAuditItem('Barcode Inventory', '/barcode-audit-list'),
                addAuditItem('Comment', '/comment-audit-list'),
                addAuditItem('Kit Tracking', '/kit-tracking-audit-list'),
                addAuditItem('Lab', '/lab-audit-list'),
                addAuditItem('Lab Invite', '/lab-invite-audit-list'),
                addAuditItem('Operator', '/operator-audit-list'),
                addAuditItem('Pipets', '/pipet-audit-list'),
                addAuditItem('Report', '/report-audit-list'),
                addAuditItem('Submitted Kits', '/submitted-kit-audit-list'),
                addAuditItem('Submitted Kit Runs', '/submitted-kit-run-audit-list')
            ]
            itemArray.splice(auditInsertIndex + 1, 0, ...auditSideBarMenu, {
                text: 'Barcode Inventory',
                ['identation-level']: 0,
                id: 6,
                route: '/barcode-list',
                icon: allIcons.jsIcon,
            });

            if (appUserContext.isStreckQCAdmin) {
                const reportSideBarIndex = itemArray.findIndex((x: any) => x.id === ReportSideBarId);
                itemArray.splice(reportSideBarIndex + 1, 0, {
                    text: 'Legacy Reports',
                    ['identation-level']: 0,
                    id: 75,
                    route: '/legacy-report-list',
                    icon: allIcons.jsIcon,
                }, {
                    text: 'Merge Labs',
                    ['identation-level']: 0,
                    id: 76,
                    route: '/merge-duplicate-lab',
                    icon: allIcons.aggregateFieldsIcon
                })
            } else if (appUserContext.isReadOnly) {
                const reportSideBarIndex = itemArray.findIndex((x: any) => x.id === ReportSideBarId);
                itemArray.splice(reportSideBarIndex + 1, 0, {
                    text: 'Legacy Reports',
                    ['identation-level']: 0,
                    id: 75,
                    route: '/legacy-report-list',
                    icon: allIcons.jsIcon,
                })
            }
        }

        setItems(itemArray);
    }, [appUserContext.adminView])

    useEffect(() => {
        //We need to wait for appUserContext to load the user so we don't create two users on first login
        if (!labContext.data && !appUserContext.isLoading) {
            getLabList();
            setInitialized(true);
        }
    }, [execute, appUserContext.isLoading]);

    return (
        <DrawerContainerShell appUser={appUserContext.appUser} handleLogOutClick={handleLogOutClick} handleDrawerExpandClick={handleDrawerExpandClick}>
            {showLabInviteWindow &&
                <Window initialWidth={500} title={'Accept or decline lab invites'} onClose={() => { setShowLabInviteWindow(false); }}>
                    <AcceptLabInvites labInvites={activeLabInvites} setLabInvites={setActiveLabInvites} closeWindow={() => { setShowLabInviteWindow(false); }}></AcceptLabInvites>
                </Window>}

            <Drawer
                expanded={drawerExpanded}
                position={'start'}
                mode={'push'}
                className='s-k-drawer-container'
                //mini={true}
                //miniWidth={80}
                width={250}
                items={data}
                item={CustomDropdownItem}
                onSelect={onSelect}
            > <DrawerContent
                style={{ height: "calc(100vh - 124px)" }}
                    className='s-k-drawer-content'>
                    {/* Announcements */}
                    {MaintenceAnnouncement()}
                    {DecommisionedAnnouncement()}

                    {/* Content */}
                    {getContent()}
                </DrawerContent>
            </Drawer>
        </DrawerContainerShell >
    );
};